'use client';

import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { ChevronLeft, ChevronRight, Eye, SearchX } from 'lucide-react';
import { ShipmentSearchResult } from '@/lib/types/types';
import Link from 'next/link';

interface ShipmentTableProps {
    results: ShipmentSearchResult[];
    vulcanId: string;
    searchQuery: string;
    currentPage: number;
    totalPages: number;   
    onPageChange: (page: number) => void;
}

export const ShipmentResultsTable = ({
    results,
    vulcanId,
    searchQuery,
    currentPage,
    totalPages, 
    onPageChange
}: ShipmentTableProps) => {

    if (!results || results.length === 0) {
        return (
            <div className="text-center p-8 space-y-4 bg-transparent">
                <div className="flex justify-center mb-4">
                    <SearchX className="w-16 h-16 text-gray-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-600">Sorry, no shipments were found for &quot;{searchQuery}&quot;</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                    Please check your input or use a different reference no. Enter the complete reference or shipment number.
                </p>
                <div className="rounded-lg p-4 inline-block text-left">
                    <h4 className="font-semibold mb-2">We find shipments for the following references:</h4>
                    <ul className="list-disc list-inside space-y-1 ">
                        <li>Tracking Number.</li>
                        <li>Booking Number.</li>
                        <li>Reference Number.</li>
                        <li>Container Number.</li>
                        <li>Shipment Number.</li>
                        <li>H/AWB Number.</li>
                        <li>Booking ID</li>
                        <li>Customer reference</li>
                        <li>Package Number.</li>
                        <li>Line B/L or SWB number</li>
                    </ul>
                </div>               
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <Table className="bg-transparent">
                <TableHeader>
                    <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Shipment Mode</TableHead>
                        <TableHead>Origin</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {results.map((result) => (
                        <TableRow key={result.id}>
                            <TableCell>{result.type.toUpperCase()}</TableCell>
                            <TableCell>{result.title}</TableCell>
                            <TableCell>{result.shipmentMode || 'N/A'}</TableCell>
                            <TableCell>{result.origin || 'N/A'}</TableCell>
                            <TableCell>{result.destination || 'N/A'}</TableCell>
                            <TableCell>{result.shipmentStatus || 'N/A'}</TableCell>
                            <TableCell>
                                <Link
                                    href={`/track_shipment/${vulcanId}/shipment_details?id=${result.id}&mode=view&fromSearch=true&q=${searchQuery}&page=${currentPage}`}
                                >
                                    <Button variant="outline"
size="sm">
                                        <Eye className="mr-2 h-4 w-4" /> Details
                                    </Button>
                                </Link>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <ChevronLeft className="mr-2 h-4 w-4" /> Previous
                    </Button>
                    <span className="text-sm">
                        Page {currentPage} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            )}
        </div>
    );
};